import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import {
  findActiveAncestor,
  getProjectTotal,
  calculateTotal,
} from "../../services/costs/calculations.js";
import { exportEstimateToExcel } from "../../services/costs/estimateExcelExport.js";
import SaveStatusIndicator from "./SaveStatusIndicator.jsx";

const UNITS = [
  { value: "pl", label: "pauschal" },
  { value: "st", label: "Stück" },
  { value: "m", label: "Laufmeter" },
  { value: "m2", label: "m²" },
  { value: "m3", label: "m³" },
  { value: "h", label: "Stunden" },
  { value: "t", label: "Tonne" },
  { value: "kg", label: "Kilogramm" },
];

const OVERRIDE_FIELDS = [
  { key: "quantity", label: "Menge", type: "number" },
  { key: "unitPrice", label: "Einheitspreis", type: "number" },
  { key: "unit", label: "Einheit", type: "unit" },
  { key: "length", label: "Länge", type: "number" },
  { key: "width", label: "Breite", type: "number" },
  { key: "height", label: "Höhe", type: "number" },
  { key: "factor", label: "Faktor", type: "number" },
];

const TagInput = React.forwardRef(({ value, onChange, suggestionIndex, onSuggestionIndexChange, allTags, currentTags, onAdd, onClose }, ref) => {
  const suggestionsRef = useRef(null);
  const filtered = value.trim()
    ? allTags.filter((t) => t.toLowerCase().includes(value.toLowerCase()) && !currentTags.includes(t))
    : [];

  useEffect(() => {
    if (ref?.current) ref.current.focus();
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestionIndex >= 0 && suggestionIndex < filtered.length) {
        onAdd(filtered[suggestionIndex]);
      } else if (value.trim()) {
        onAdd(value.trim());
      }
    } else if (e.key === "Escape") {
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      onSuggestionIndexChange(Math.min(suggestionIndex + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      onSuggestionIndexChange(Math.max(suggestionIndex - 1, -1));
    }
  };

  return (
    <span className="tag-input-wrapper" onClick={(e) => e.stopPropagation()}>
      <input
        ref={ref}
        className="tag-input"
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); onSuggestionIndexChange(-1); }}
        onKeyDown={handleKeyDown}
        onBlur={() => setTimeout(onClose, 150)}
        placeholder="Tag..."
      />
      {filtered.length > 0 && (
        <div className="tag-suggestions" ref={suggestionsRef}>
          {filtered.map((tag, i) => (
            <div
              key={tag}
              className={`tag-suggestion ${i === suggestionIndex ? "active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); onAdd(tag); }}
            >
              {tag}
            </div>
          ))}
        </div>
      )}
    </span>
  );
});

const formatSwissCurrency = (amount) => {
  return amount
    .toLocaleString("de-CH", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    .replace(/,/g, "'");
};

const EstimateEditor = ({
  tree,
  expanded,
  levelFilter,
  showInactive,
  isSaving,
  lastSavedAt,
  lastSaveKind,
  isDirty,
  editCell,
  toggleActive,
  deactivateParentForChild,
  addChild4Digit,
  deleteNode,
  toggleExpand,
  saveBatch,
  undo,
  undoStack,
  beginUndoBatch,
  endUndoBatch,
  readOnly = false,
  variants = [],
  getEffectiveValue,
  getVariantConflicts,
  applyVariantOverrides,
  addPositionToVariant,
  removePositionFromVariant,
  isFullscreen = false,
  toggleFullscreen,
  searchTerm = "",
  activeFilters = [],
  filterSums = false,
  customColumns = [],
  addCustomColumn,
  deleteCustomColumn,
  renameCustomColumn,
  editCustomField,
  estimateName = "",
  linkedProject = null,
  currentPhase = "",
  canManageMembers = false,
  onManageMembers,
}) => {
  const [editingCell, setEditingCell] = useState(null);
  const [localValue, setLocalValue] = useState("");
  const [editingTags, setEditingTags] = useState(null); // { code }
  const [tagInput, setTagInput] = useState("");
  const [tagSuggestionIndex, setTagSuggestionIndex] = useState(-1);
  const tagInputRef = useRef(null);
  const [showExtraColumns, setShowExtraColumns] = useState(true);
  const [newColName, setNewColName] = useState("");
  const [editingColId, setEditingColId] = useState(null);
  const [editingColName, setEditingColName] = useState("");
  const [editingCustomTags, setEditingCustomTags] = useState(null); // { code, colId }
  const [customTagInput, setCustomTagInput] = useState("");
  const [customTagSuggestionIndex, setCustomTagSuggestionIndex] = useState(-1);
  const customTagInputRef = useRef(null);
  const inputRef = useRef(null);
  const tableContainerRef = useRef(null);
  const breadcrumbRef = useRef(null);
  const [stickyAncestors, setStickyAncestors] = useState([]);
  const stickyAncestorsRef = useRef([]);
  const isProcessingEditRef = useRef(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState(null);
  // Override modal state
  const [overrideModal, setOverrideModal] = useState(null);
  const [overrideValues, setOverrideValues] = useState({});

  // Column resize state
  const colWidthsRef = useRef({
    expand: 110, code: 60, name: 200, source: 80, tag: 150,
    dimL: 60, dimW: 60, dimH: 60, qty: 60, unit: 80, price: 100, factor: 60, total: 110, action: 50, addCol: 100,
  });
  const [colWidths, setColWidths] = useState(() => ({ ...colWidthsRef.current }));
  const [customColWidths, setCustomColWidths] = useState({});
  const customColWidthsRef = useRef({});
  const resizingRef = useRef(null);

  const onResizeStart = useCallback((e, colKey, isCustom = false) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = isCustom
      ? (customColWidthsRef.current[colKey] || 150)
      : (colWidthsRef.current[colKey] || 100);

    const onMouseMove = (ev) => {
      const diff = ev.clientX - startX;
      const newW = Math.max(30, startW + diff);
      if (isCustom) {
        customColWidthsRef.current = { ...customColWidthsRef.current, [colKey]: newW };
        setCustomColWidths({ ...customColWidthsRef.current });
      } else {
        colWidthsRef.current = { ...colWidthsRef.current, [colKey]: newW };
        setColWidths({ ...colWidthsRef.current });
      }
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      resizingRef.current = null;
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    resizingRef.current = colKey;
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);

  const ResizeHandle = useCallback(({ colKey, isCustom }) => (
    <span
      className="col-resize-handle"
      onMouseDown={(e) => onResizeStart(e, colKey, isCustom)}
    />
  ), [onResizeStart]);

  // Compute variant-aware tree
  const effectiveTree = useMemo(() => {
    if (!applyVariantOverrides) return tree;
    const activeVariants = variants.filter((v) => v.isActive);
    if (activeVariants.length === 0) return tree;
    return applyVariantOverrides(tree);
  }, [tree, variants, applyVariantOverrides]);

  // Conflicts map
  const conflicts = useMemo(() => {
    return getVariantConflicts ? getVariantConflicts() : {};
  }, [getVariantConflicts, variants]);

  // Active variants for context menu
  const activeVariants = useMemo(() => variants.filter((v) => v.isActive), [variants]);

  // Collect all unique tags across the tree for autocomplete
  const allTags = useMemo(() => {
    const tagSet = new Set();
    Object.values(tree).forEach((node) => {
      if (node.tags) node.tags.forEach((t) => tagSet.add(t));
    });
    return [...tagSet].sort();
  }, [tree]);

  // Collect all unique custom tags per column for autocomplete
  const allCustomTags = useMemo(() => {
    const result = {};
    for (const col of customColumns) {
      const tagSet = new Set();
      Object.values(tree).forEach((node) => {
        const vals = node.customFields?.[col.id];
        if (Array.isArray(vals)) vals.forEach((t) => tagSet.add(t));
      });
      result[col.id] = [...tagSet].sort();
    }
    return result;
  }, [tree, customColumns]);

  const visibleCustomColumns = useMemo(
    () => (customColumns || []).filter((c) => !c.hidden).sort((a, b) => a.order - b.order),
    [customColumns]
  );

  // Compute filteredCodes from effectiveTree
  const filteredCodes = useMemo(() => {
    if (!searchTerm.trim() && activeFilters.length === 0) return null;

    const matchingCodes = new Set();

    const getParentCodes = (node) => {
      const parents = [];
      let current = node;
      while (current && current.parentCode) {
        parents.push(current.parentCode);
        current = effectiveTree[current.parentCode];
        if (!current) break;
      }
      return parents;
    };

    const fieldPrefixes = {
      "source:": "source",
      "tag:": "tags",
      "ep:": "unitPrice",
      "total:": "totalComputed",
    };

    const nodeMatchesTerm = (node, rawTerm) => {
      const term = rawTerm.trim();
      if (!term) return true;

      let query = term;
      let specificField = null;

      for (const [prefix, field] of Object.entries(fieldPrefixes)) {
        if (term.toLowerCase().startsWith(prefix)) {
          specificField = field;
          query = term.substring(prefix.length).trim();
          break;
        }
      }

      const queryLower = query.toLowerCase();

      if (specificField) {
        if (specificField === "tags") {
          const tags = node.tags || [];
          return tags.some((t) => t.toLowerCase().includes(queryLower));
        } else if (specificField === "unitPrice" || specificField === "totalComputed") {
          return node[specificField] != null && node[specificField].toString().includes(query);
        } else {
          return !!(node[specificField] && node[specificField].toLowerCase().includes(queryLower));
        }
      }

      const tags = (node.tags || []).map((t) => t.toLowerCase());
      const customFieldTags = Object.values(node.customFields || {}).flatMap((v) =>
        Array.isArray(v) ? v.map((t) => t.toLowerCase()) : []
      );
      return (
        node.code.toLowerCase().includes(queryLower) ||
        (node.name && node.name.toLowerCase().includes(queryLower)) ||
        (node.source && node.source.toLowerCase().includes(queryLower)) ||
        tags.some((t) => t.includes(queryLower)) ||
        customFieldTags.some((t) => t.includes(queryLower))
      );
    };

    const raw = searchTerm.trim();
    let searchTerms = null;
    let searchCombinator = "every";
    if (raw) {
      if (raw.includes("&")) {
        searchTerms = raw.split("&").map((t) => t.trim()).filter((t) => t.length > 0);
        searchCombinator = "every";
      } else if (raw.includes(";")) {
        searchTerms = raw.split(";").map((t) => t.trim()).filter((t) => t.length > 0);
        searchCombinator = "some";
      } else {
        searchTerms = [raw];
        searchCombinator = "every";
      }
    }

    Object.values(effectiveTree).forEach((node) => {
      let matchesSearch = true;
      let matchesFilters = true;

      if (searchTerms && searchTerms.length > 0) {
        matchesSearch = searchTerms[searchCombinator]((term) => nodeMatchesTerm(node, term));
      }

      if (activeFilters.length > 0) {
        matchesFilters = activeFilters.every((filter) => {
          const col = filter.column.toLowerCase();
          const val = filter.value.toLowerCase();
          if (col === "source") return node.source && node.source.toLowerCase().includes(val);
          if (col === "tag") return (node.tags || []).some((t) => t.toLowerCase().includes(val));
          if (col === "ep") return node.unitPrice != null && node.unitPrice.toString().includes(filter.value);
          if (col === "total") return node.totalComputed != null && node.totalComputed.toString().includes(filter.value);
          // Check custom columns by name
          const matchedCol = customColumns.find((c) => c.name.toLowerCase() === col);
          if (matchedCol) {
            const vals = node.customFields?.[matchedCol.id];
            return Array.isArray(vals) && vals.some((t) => t.toLowerCase().includes(val));
          }
          return node.name && node.name.toLowerCase().includes(val);
        });
      }

      if (matchesSearch && matchesFilters) {
        matchingCodes.add(node.code);
        getParentCodes(node).forEach((pc) => matchingCodes.add(pc));
      }
    });

    return matchingCodes;
  }, [effectiveTree, searchTerm, activeFilters, customColumns]);

  // Compute filtered total for a node: only sums matching leaf nodes
  const getFilteredTotal = useCallback((node) => {
    if (!filteredCodes || !filterSums) return node.totalComputed;

    // Leaf node (active with own value): return its total if it matches
    if (node.active && (!node.children || node.children.length === 0 || node.totalComputed > 0)) {
      // If this node is a leaf or has its own calculated value, check if it's in the filter
      if (!node.children || node.children.length === 0) {
        return filteredCodes.has(node.code) ? node.totalComputed : 0;
      }
      // Parent with own value: if active with direct calculation, use its total
      if (node.active && node.totalComputed > 0 && node.children?.every(c => !effectiveTree[c]?.active)) {
        return filteredCodes.has(node.code) ? node.totalComputed : 0;
      }
    }

    // Parent node: sum filtered children recursively
    if (!node.children || node.children.length === 0) return 0;
    return node.children.reduce((sum, childCode) => {
      const child = effectiveTree[childCode];
      if (!child || !filteredCodes.has(childCode)) return sum;
      return sum + getFilteredTotal(child);
    }, 0);
  }, [effectiveTree, filteredCodes, filterSums]);

  // Excel export handler
  const handleExcelExport = useCallback(() => {
    exportEstimateToExcel({
      tree: effectiveTree,
      filteredCodes,
      showInactive,
      estimateName,
      linkedProject,
      currentPhase,
      visibleCustomColumns,
    });
  }, [effectiveTree, filteredCodes, showInactive, estimateName, linkedProject, currentPhase, visibleCustomColumns]);

  // Focus input when editing starts
  useEffect(() => {
    if (editingCell && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [editingCell]);

  // Auto-Save (30s nach der letzten Aenderung, spaetestens nach 10 Minuten) und
  // die beforeunload-Warnung liegen zentral in useEstimateEditor, damit sie auch
  // fuer die Kostenkontrolle gelten und nie zwei Speichervorgaenge parallel laufen.

  // Sticky breadcrumb: find ancestor chain of the first row scrolled past the header
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    let rafId = null;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const thead = container.querySelector("thead:first-of-type");
        if (!thead) return;

        const theadHeight = thead.offsetHeight;
        // Account for current breadcrumb height so rows hide precisely behind it
        const bc = breadcrumbRef.current;
        const bcHeight = bc ? bc.offsetHeight : 0;
        const cutoff = container.scrollTop + theadHeight + bcHeight;

        const rows = container.querySelectorAll("tbody tr[data-code][data-level]");
        const scrolledPast = [];

        for (const row of rows) {
          // A row is "scrolled past" when its top edge is above the cutoff
          if (row.offsetTop < cutoff) {
            scrolledPast.push({
              code: row.dataset.code,
              level: parseInt(row.dataset.level, 10),
              hasChildren: row.dataset.haschildren === "true",
            });
          } else {
            break;
          }
        }

        if (scrolledPast.length === 0) {
          if (stickyAncestorsRef.current.length > 0) {
            stickyAncestorsRef.current = [];
            setStickyAncestors([]);
          }
          return;
        }

        // Build ancestor chain: only parents with children
        // Walk through scrolled-past rows, keeping the last one at each level
        const ancestors = [];
        for (const item of scrolledPast) {
          // Trim deeper or same-level entries when a new sibling/parent appears
          while (ancestors.length > 0 && ancestors[ancestors.length - 1].level >= item.level) {
            ancestors.pop();
          }
          // Only include nodes that have children (are actual parents)
          if (item.hasChildren) {
            ancestors.push(item);
          }
        }

        const codes = ancestors.map((a) => a.code);

        const prev = stickyAncestorsRef.current;
        if (codes.length !== prev.length || codes.some((c, i) => c !== prev[i])) {
          stickyAncestorsRef.current = codes;
          setStickyAncestors(codes);
        }
      });
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    // Initial run
    setTimeout(handleScroll, 50);
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [effectiveTree, expanded]);

  // Keyboard shortcuts: Ctrl+Z undo, Ctrl+S save
  useEffect(() => {
    const handleKeyboard = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        if (editingCell) return;
        e.preventDefault();
        if (undo && undoStack?.length > 0) undo();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "s") {
        e.preventDefault();
        if (!isSaving && isDirty && !readOnly) saveBatch();
      }
    };
    document.addEventListener("keydown", handleKeyboard);
    return () => document.removeEventListener("keydown", handleKeyboard);
  }, [editingCell, undo, undoStack, isSaving, isDirty, readOnly, saveBatch]);

  // Close context menu on click anywhere
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener("click", handleClick);
      return () => document.removeEventListener("click", handleClick);
    }
  }, [contextMenu]);

  const hasActiveDescendants = (nodeCode) => {
    const node = effectiveTree[nodeCode];
    if (!node?.children) return false;
    for (const childCode of node.children) {
      if (effectiveTree[childCode]?.active) return true;
      if (hasActiveDescendants(childCode)) return true;
    }
    return false;
  };

  const startEdit = (code, field, currentValue) => {
    if (readOnly) return;
    setEditingCell({ code, field });
    setLocalValue(currentValue?.toString() || "");
  };

  const handleCellEdit = (code, field, value) => {
    if (isProcessingEditRef.current) return;

    const calcFields = ["quantity", "length", "width", "height", "unitPrice", "unit", "factor"];
    const node = effectiveTree[code];
    const isParent = node?.children?.length > 0;

    if (isParent && calcFields.includes(field) && hasActiveDescendants(code)) {
      isProcessingEditRef.current = true;
      if (!window.confirm(
        "Wenn Sie eine übergeordnete Position bearbeiten, werden alle untergeordneten Positionen deaktiviert. Fortfahren?"
      )) {
        setEditingCell(null);
        isProcessingEditRef.current = false;
        return;
      }
      isProcessingEditRef.current = false;
    }

    beginUndoBatch();
    try {
      editCell(code, field, value);

      // Auto-unit detection
      if (field === "length" || field === "width" || field === "height") {
        const n = effectiveTree[code];
        const length = field === "length" ? parseFloat(value) : n.length || 0;
        const width = field === "width" ? parseFloat(value) : n.width || 0;
        const height = field === "height" ? parseFloat(value) : n.height || 0;

        let newUnit = n.unit;
        if (length > 0 && width > 0 && height > 0) newUnit = "m3";
        else if (length > 0 && width > 0) newUnit = "m2";
        else if (length > 0) newUnit = "m";
        if (newUnit !== n.unit) editCell(code, "unit", newUnit);
      }

      if (field === "quantity") {
        const n = effectiveTree[code];
        const quantity = parseFloat(value) || 0;
        if (quantity > 0 && !n.length && !n.width && !n.height) {
          editCell(code, "unit", "st");
        }
      }

      // Auto-activation for leaf nodes
      if (!["source", "name", "comment"].includes(field)) {
        const n = effectiveTree[code];
        if (n && n.totalComputed > 0 && !n.active && (!n.children || n.children.length === 0)) {
          toggleActive(code);
        }
      }
    } finally {
      endUndoBatch();
    }

    setEditingCell(null);
    setLocalValue("");
  };

  const handleKeyDown = (e, code, field) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellEdit(code, field, field === "unit" ? localValue : localValue);

      // Enter → move to same field in next visible row
      const visibleNodes = getVisibleNodeList();
      const currentIdx = visibleNodes.findIndex((n) => n.code === code);
      if (currentIdx >= 0 && currentIdx < visibleNodes.length - 1) {
        const nextNode = visibleNodes[currentIdx + 1];
        setTimeout(() => startEdit(nextNode.code, field, nextNode[field]), 10);
      }
    } else if (e.key === "Tab") {
      e.preventDefault();
      handleCellEdit(code, field, localValue);

      const fields = [
        "name", "source",
        "length", "width", "height",
        "quantity", "unit", "unitPrice", "factor",
      ];
      const currentIndex = fields.indexOf(field);

      if (e.shiftKey) {
        if (currentIndex > 0) {
          const prevField = fields[currentIndex - 1];
          setTimeout(() => startEdit(code, prevField, effectiveTree[code]?.[prevField]), 10);
        }
      } else {
        if (currentIndex < fields.length - 1) {
          const nextField = fields[currentIndex + 1];
          setTimeout(() => startEdit(code, nextField, effectiveTree[code]?.[nextField]), 10);
        } else {
          // Next row, first field
          const visibleNodes = getVisibleNodeList();
          const currentIdx = visibleNodes.findIndex((n) => n.code === code);
          if (currentIdx < visibleNodes.length - 1) {
            const nextNode = visibleNodes[currentIdx + 1];
            setTimeout(() => startEdit(nextNode.code, "name", nextNode.name), 10);
          }
        }
      }
    } else if (e.key === "Escape") {
      setEditingCell(null);
      setLocalValue("");
    }
  };

  const getVisibleNodeList = useCallback(() => {
    const result = [];
    const collectVisible = (node, depth) => {
      if (!node) return;
      if (showInactive || node.active || node.level === 1 ||
          (node.children?.length > 0 && node.totalComputed > 0)) {
        result.push(node);
      }
      if (expanded[node.code] && node.children) {
        for (const childCode of node.children) {
          collectVisible(effectiveTree[childCode], depth + 1);
        }
      }
    };

    Object.values(effectiveTree)
      .filter((n) => n.level === 1)
      .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
      .forEach((n) => collectVisible(n, 0));

    return result;
  }, [effectiveTree, expanded, showInactive]);

  // Check if a node has children in the tree
  const hasChildren = (node) => {
    return node.children && node.children.length > 0;
  };

  // Context menu handler (Varianten-Einträge + Kommentar; öffnet auch ohne Varianten)
  const handleContextMenu = (e, node) => {
    if (readOnly) return;
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      bkpCode: node.code,
      nodeName: node.name || node.code,
    });
  };

  // Open override modal for adding a position to a variant
  const openOverrideModal = (variantId, bkpCode) => {
    const node = effectiveTree[bkpCode];
    if (!node) return;
    setOverrideValues({
      quantity: node.quantity || 0,
      unitPrice: node.unitPrice || 0,
      unit: node.unit || "pl",
      length: node.length || 0,
      width: node.width || 0,
      height: node.height || 0,
      factor: node.factor || 1,
    });
    setOverrideModal({ variantId, bkpCode, nodeName: node.name || bkpCode });
    setContextMenu(null);
  };

  const handleOverrideSave = () => {
    if (!overrideModal || !addPositionToVariant) return;
    const { variantId, bkpCode } = overrideModal;

    // Only include fields that differ from base node
    const baseNode = tree[bkpCode];
    const overrides = {};
    for (const field of OVERRIDE_FIELDS) {
      const baseVal = baseNode?.[field.key];
      const overrideVal = overrideValues[field.key];
      if (field.type === "number") {
        if ((parseFloat(overrideVal) || 0) !== (parseFloat(baseVal) || 0)) {
          overrides[field.key] = parseFloat(overrideVal) || 0;
        }
      } else {
        if (overrideVal !== (baseVal || "")) {
          overrides[field.key] = overrideVal;
        }
      }
    }

    addPositionToVariant(variantId, bkpCode, overrides);
    setOverrideModal(null);
    setOverrideValues({});
  };

  // Check if a node has an active variant override
  const getNodeVariantInfo = useCallback((bkpCode) => {
    if (!getEffectiveValue) return null;
    return getEffectiveValue(bkpCode);
  }, [getEffectiveValue]);

  const renderCell = (node, field, type = "text") => {
    const activeAncestorCode = findActiveAncestor(node, effectiveTree);
    const parentDisabled = !!activeAncestorCode;
    const isCalcField = ["length", "width", "height", "quantity", "unit", "unitPrice", "factor"].includes(field);
    const isEditableField = ["source", ...["length", "width", "height", "quantity", "unit", "unitPrice", "factor"]].includes(field);
    const fieldIsZero = isEditableField && (node[field] === 0 || node[field] === "0" || !node[field]);
    const isEmptyParent = isCalcField && !node.active && node.children?.length > 0 && node.totalComputed === 0;
    const isDimensionField = ["length", "width", "height", "quantity"].includes(field);
    const isIgnoredByPauschal = isDimensionField && node.unit === "pl";
    const shouldBeGrayed = (node.active && fieldIsZero) || isEmptyParent || isIgnoredByPauschal;
    // Bei Einheit "pl" (Pauschal) zaehlt nur EP x Faktor - Mengenfelder werden ignoriert,
    // deshalb auch editier-gesperrt (Klick wird verworfen, Tooltip erklaert den Grund).
    const shouldBeDisabled = parentDisabled || readOnly || isIgnoredByPauschal;
    const pauschalTooltip = isIgnoredByPauschal
      ? "Bei Pauschal-Positionen wird nur Einheitspreis × Faktor verwendet."
      : undefined;
    const isEditing = editingCell?.code === node.code && editingCell?.field === field;

    // Parent aggregating from children: show calc fields as empty
    const isAggregatingParent = isCalcField && !node.active && node.children?.length > 0 && node.totalComputed > 0;
    if (isAggregatingParent) {
      return <td className={`${type === "number" ? "number-cell" : type === "unit" ? "unit-cell" : ""} disabled`}></td>;
    }

    // Check for variant override on this field
    const variantInfo = getNodeVariantInfo(node.code);
    const hasOverride = variantInfo && variantInfo.overrides && field in variantInfo.overrides;

    if (type === "unit") {
      return (
        <td
          className={`unit-cell ${shouldBeDisabled ? "disabled" : "editable"} ${shouldBeGrayed ? "grayed" : ""} ${hasOverride ? "variant-override" : ""}`}
          onClick={() => {
            if (shouldBeDisabled) return;
            if (!node.active) {
              const safe = !node.children?.length || !hasActiveDescendants(node.code);
              if (safe) toggleActive(node.code);
              setTimeout(() => startEdit(node.code, field, node[field]), safe ? 100 : 0);
            } else {
              startEdit(node.code, field, node[field]);
            }
          }}
          onDoubleClick={() => {
            if (readOnly) return;
            if (activeAncestorCode) {
              if (window.confirm("Übergeordnete Position deaktivieren?")) {
                deactivateParentForChild(activeAncestorCode, node.code);
                setTimeout(() => startEdit(node.code, field, node[field]), 100);
              }
            }
          }}
        >
          {hasOverride && <span className="variant-badge" title={`Variante: ${variantInfo.variantName}`}>V</span>}
          {isEditing ? (
            <select
              ref={inputRef}
              value={localValue}
              onChange={(e) => handleCellEdit(node.code, field, e.target.value)}
              onBlur={() => setEditingCell(null)}
              onKeyDown={(e) => handleKeyDown(e, node.code, field)}
            >
              {UNITS.map((u) => (
                <option key={u.value} value={u.value}>{u.label}</option>
              ))}
            </select>
          ) : (
            UNITS.find((u) => u.value === node[field])?.label || node[field]
          )}
        </td>
      );
    }

    if (type === "number") {
      return (
        <td
          className={`number-cell ${shouldBeDisabled ? "disabled" : "editable"} ${shouldBeGrayed ? "grayed" : ""} ${hasOverride ? "variant-override" : ""}`}
          title={pauschalTooltip}
          onClick={() => {
            if (shouldBeDisabled) return;
            if (!node.active) {
              const safe = !node.children?.length || !hasActiveDescendants(node.code);
              if (safe) toggleActive(node.code);
              setTimeout(() => startEdit(node.code, field, node[field]), safe ? 100 : 0);
            } else {
              startEdit(node.code, field, node[field]);
            }
          }}
          onDoubleClick={() => {
            if (readOnly) return;
            if (activeAncestorCode) {
              if (window.confirm("Übergeordnete Position deaktivieren?")) {
                deactivateParentForChild(activeAncestorCode, node.code);
                setTimeout(() => startEdit(node.code, field, node[field]), 100);
              }
            }
          }}
        >
          {hasOverride && <span className="variant-badge" title={`Variante: ${variantInfo.variantName}`}>V</span>}
          {isEditing ? (
            <input
              ref={inputRef}
              type="number"
              step="0.01"
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              onBlur={() => handleCellEdit(node.code, field, parseFloat(localValue) || 0)}
              onKeyDown={(e) => handleKeyDown(e, node.code, field)}
            />
          ) : field === "unitPrice" ? (
            `CHF ${formatSwissCurrency(parseFloat(node[field] || 0))}`
          ) : field === "factor" ? (
            parseFloat(node[field] || 1).toFixed(2)
          ) : (
            node[field] || 0
          )}
        </td>
      );
    }

    // text
    return (
      <td
        className={`${field}-cell ${shouldBeDisabled ? "disabled" : "editable"} ${shouldBeGrayed ? "grayed" : ""}`}
        onClick={() => {
          if (shouldBeDisabled) return;
          startEdit(node.code, field, node[field]);
        }}
      >
        {isEditing ? (
          <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => handleCellEdit(node.code, field, localValue)}
            onKeyDown={(e) => handleKeyDown(e, node.code, field)}
          />
        ) : (
          node[field] || ""
        )}
      </td>
    );
  };

  const renderTree = () => {
    const rows = [];

    // Check if a node has any active descendant (recursively).
    // Used so that an inactive parent with a freshly-added active (but still
    // zero-total) child remains visible in the tree.
    const hasActiveDescendant = (n) => {
      if (!n) return false;
      if (n.active) return true;
      for (const c of (n.children || [])) {
        const child = effectiveTree[c];
        if (child && hasActiveDescendant(child)) return true;
      }
      return false;
    };

    const renderNodeAndChildren = (node, depth = 0) => {
      if (!node) return;

      // Search filter
      if (filteredCodes && !filteredCodes.has(node.code)) return;

      const isExpanded = expanded[node.code];
      const nodeHasChildren = hasChildren(node);
      const parentDisabled = !!findActiveAncestor(node, effectiveTree);
      const variantInfo = getNodeVariantInfo(node.code);
      const hasConflict = !!conflicts[node.code];

      // Inactive filter: hide inactive nodes with no content,
      // but keep them if any descendant is active (e.g. user just added one)
      if (!showInactive && !node.active && node.level !== 1) {
        const hasContent = node.children?.length > 0 && node.totalComputed > 0;
        const keepForActiveDescendant = hasActiveDescendant(node);
        if (!hasContent && !keepForActiveDescendant) return;
      }

      rows.push(
        <tr
          key={`node-${node.code}`}
          className={`bkp-row level-${node.level} ${parentDisabled ? "disabled" : ""} ${!node.active ? "inactive-row" : ""} ${hasConflict ? "conflict-row" : ""}`}
          data-code={node.code}
          data-level={node.level}
          data-haschildren={nodeHasChildren}
          onContextMenu={(e) => handleContextMenu(e, node)}
        >
          <td className="expand-cell" style={{ paddingLeft: `${depth * 20}px` }}>
            {nodeHasChildren && (
              <button
                className="expand-button"
                onClick={() => toggleExpand(node.code)}
              >
                {isExpanded ? "\u25BC" : "\u25B6"}
              </button>
            )}
          </td>
          <td className="code-cell">
            <span>{node.code}</span>
          </td>

          <td
            className={`name-cell ${parentDisabled || readOnly ? "disabled" : "editable"}`}
            onClick={() => !(parentDisabled || readOnly) && startEdit(node.code, "name", node.name)}
          >
            {editingCell?.code === node.code && editingCell?.field === "name" ? (
              <input
                ref={inputRef}
                type="text"
                value={localValue}
                onChange={(e) => setLocalValue(e.target.value)}
                onBlur={() => handleCellEdit(node.code, "name", localValue)}
                onKeyDown={(e) => handleKeyDown(e, node.code, "name")}
              />
            ) : (
              <div className="name-content">
                <span>{node.name || ""}</span>
                <div className="note-indicators">
                  {node.note && <span className="note-indicator" title="Notiz vorhanden" />}
                  {node.isRisk && <span className="risk-indicator" title="Risiko" />}
                  {variantInfo && <span className="variant-indicator" title={`Variante: ${variantInfo.variantName}`}>V</span>}
                </div>
              </div>
            )}
          </td>

          {showExtraColumns && renderCell(node, "source", "text")}
          {showExtraColumns && (
            <td className="tag-cell" onClick={() => !readOnly && setEditingTags({ code: node.code })}>
              <div className="tag-cell-content">
                {(node.tags || []).map((tag) => (
                  <span key={tag} className="tag-badge">
                    {tag}
                    {!readOnly && (
                      <button
                        className="tag-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newTags = (node.tags || []).filter((t) => t !== tag);
                          editCell(node.code, "tags", newTags);
                        }}
                      >
                        &times;
                      </button>
                    )}
                  </span>
                ))}
                {editingTags?.code === node.code && !readOnly && (
                  <TagInput
                    ref={tagInputRef}
                    value={tagInput}
                    onChange={setTagInput}
                    suggestionIndex={tagSuggestionIndex}
                    onSuggestionIndexChange={setTagSuggestionIndex}
                    allTags={allTags}
                    currentTags={node.tags || []}
                    onAdd={(tag) => {
                      const newTags = [...new Set([...(node.tags || []), tag])];
                      editCell(node.code, "tags", newTags);
                      setTagInput("");
                      setTagSuggestionIndex(-1);
                    }}
                    onClose={() => {
                      setEditingTags(null);
                      setTagInput("");
                      setTagSuggestionIndex(-1);
                    }}
                  />
                )}
              </div>
            </td>
          )}
          {showExtraColumns && visibleCustomColumns.map((col) => {
            const customTags = node.customFields?.[col.id] || [];
            const isEditingThis = editingCustomTags?.code === node.code && editingCustomTags?.colId === col.id;
            return (
              <td key={col.id} className="tag-cell custom-tag-cell" onClick={() => !readOnly && setEditingCustomTags({ code: node.code, colId: col.id })}>
                <div className="tag-cell-content">
                  {(Array.isArray(customTags) ? customTags : []).map((tag) => (
                    <span key={tag} className="tag-badge">
                      {tag}
                      {!readOnly && (
                        <button
                          className="tag-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            const newTags = customTags.filter((t) => t !== tag);
                            editCustomField(node.code, col.id, newTags);
                          }}
                        >
                          &times;
                        </button>
                      )}
                    </span>
                  ))}
                  {isEditingThis && !readOnly && (
                    <TagInput
                      ref={customTagInputRef}
                      value={customTagInput}
                      onChange={setCustomTagInput}
                      suggestionIndex={customTagSuggestionIndex}
                      onSuggestionIndexChange={setCustomTagSuggestionIndex}
                      allTags={allCustomTags[col.id] || []}
                      currentTags={customTags}
                      onAdd={(tag) => {
                        const newTags = [...new Set([...customTags, tag])];
                        editCustomField(node.code, col.id, newTags);
                        setCustomTagInput("");
                        setCustomTagSuggestionIndex(-1);
                      }}
                      onClose={() => {
                        setEditingCustomTags(null);
                        setCustomTagInput("");
                        setCustomTagSuggestionIndex(-1);
                      }}
                    />
                  )}
                </div>
              </td>
            );
          })}
          {showExtraColumns && !readOnly && <td></td>}
          {renderCell(node, "length", "number")}
          {renderCell(node, "width", "number")}
          {renderCell(node, "height", "number")}
          {renderCell(node, "quantity", "number")}
          {renderCell(node, "unit", "unit")}
          {renderCell(node, "unitPrice", "number")}
          {renderCell(node, "factor", "number")}

          <td className={`total-cell ${!node.active && node.totalComputed === 0 ? "inactive" : ""}`}>
            {formatSwissCurrency(filteredCodes && filterSums ? getFilteredTotal(node) : node.totalComputed)}
          </td>

          {!readOnly && (
            <td className="action-cell">
              {node.level >= 2 && node.level < 5 && (
                <button
                  className="add-button"
                  onClick={() => addChild4Digit(node.code)}
                  title="Unterposition hinzufügen"
                  disabled={parentDisabled}
                >
                  +
                </button>
              )}
              {node.level >= 2 && (node.level >= 4 || node.isUserAdded) && (
                <button
                  className="delete-button"
                  onClick={() => {
                    const hasKids = node.children?.length > 0;
                    const msg = hasKids
                      ? `Position ${node.code} und alle untergeordneten Positionen löschen?`
                      : `Position ${node.code} löschen?`;
                    if (window.confirm(msg)) deleteNode(node.code);
                  }}
                  title="Position löschen"
                  disabled={parentDisabled}
                >
                  &times;
                </button>
              )}
            </td>
          )}
        </tr>
      );

      // Kommentarzeile: direkt an die BKP-Position angehängt, nur Text in der
      // Bezeichnungs-Spalte (kursiv), alle übrigen Zellen bewusst leer.
      const isEditingComment =
        editingCell?.code === node.code && editingCell?.field === "comment";
      if (node.comment || isEditingComment) {
        const restCols =
          (showExtraColumns ? 2 + visibleCustomColumns.length + (!readOnly ? 1 : 0) : 0) +
          8 + // Länge, Breite, Höhe, Menge, Einheit, EP, Faktor, Total
          (!readOnly ? 1 : 0);
        rows.push(
          <tr key={`comment-${node.code}`} className={`comment-row level-${node.level}`}>
            <td className="expand-cell" style={{ paddingLeft: `${depth * 20}px` }}></td>
            <td className="code-cell"></td>
            <td
              className={`name-cell comment-cell ${readOnly ? "" : "editable"}`}
              onClick={() => {
                if (readOnly || isEditingComment) return;
                startEdit(node.code, "comment", node.comment);
              }}
            >
              {isEditingComment ? (
                <input
                  ref={inputRef}
                  type="text"
                  value={localValue}
                  placeholder="Kommentar..."
                  onChange={(e) => setLocalValue(e.target.value)}
                  onBlur={() => handleCellEdit(node.code, "comment", localValue)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleCellEdit(node.code, "comment", localValue);
                    } else if (e.key === "Escape") {
                      setEditingCell(null);
                      setLocalValue("");
                    }
                  }}
                />
              ) : (
                <span className="comment-text">{node.comment}</span>
              )}
            </td>
            <td colSpan={restCols}></td>
          </tr>
        );
      }

      // levelFilter >= 5: Suchresultate auch in tiefen Levels (6+) einblenden.
      const levelFilterShowsAll = levelFilter >= 5;
      if ((isExpanded || (filteredCodes && (levelFilterShowsAll || node.level < levelFilter))) && node.children) {
        for (const childCode of node.children) {
          if (effectiveTree[childCode]) {
            renderNodeAndChildren(effectiveTree[childCode], depth + 1);
          }
        }
      }
    };

    Object.values(effectiveTree)
      .filter((n) => n.level === 1)
      .sort((a, b) => parseInt(a.code, 10) - parseInt(b.code, 10))
      .forEach((n) => renderNodeAndChildren(n, 0));

    return rows;
  };

  const projectTotal = getProjectTotal(effectiveTree);
  const filteredTotal = useMemo(() => {
    if (!filteredCodes || !filterSums) return projectTotal;
    return Object.values(effectiveTree)
      .filter((n) => n.level === 1)
      .reduce((sum, node) => sum + getFilteredTotal(node), 0);
  }, [effectiveTree, filteredCodes, filterSums, projectTotal, getFilteredTotal]);
  const treeHasData = Object.values(effectiveTree).some((n) => n.active);

  return (
    <div className="estimate-editor">
      <div className="editor-header">
        <div className="editor-header-left">
          <span className="total-label">
            Total: CHF {formatSwissCurrency(filteredTotal)}
            {filteredCodes && filterSums && filteredTotal !== projectTotal && (
              <span className="filtered-total-hint"> (gefiltert)</span>
            )}
          </span>
        </div>
        <div className="editor-header-right">
          <SaveStatusIndicator
            isDirty={isDirty}
            isSaving={isSaving}
            lastSavedAt={lastSavedAt}
            lastSaveKind={lastSaveKind}
          />
          {canManageMembers && onManageMembers && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={onManageMembers}
              title="Team-Mitglieder dem Projekt zuordnen (gleicher Zugriff wie Pendenzen, Termine, Controlling)"
            >
              Personen zuordnen
            </button>
          )}
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleExcelExport}
            title="Als Excel exportieren"
          >
            Excel Export
          </button>
          {!readOnly && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => saveBatch("manual")}
              disabled={isSaving || !isDirty}
            >
              {isSaving ? "Speichern..." : "Speichern"}
            </button>
          )}
          {toggleFullscreen && (
            <button
              className="fullscreen-btn"
              onClick={toggleFullscreen}
              title={isFullscreen ? "Vollbild beenden" : "Vollbild"}
            >
              {isFullscreen ? "\u2716" : "\u26F6"}
            </button>
          )}
        </div>
      </div>

      {!treeHasData && (
        <div className="empty-hint">
          Positionen aufklappen und Werte eintragen
        </div>
      )}

      <div className="bkp-table-container" ref={tableContainerRef}>
        <table className="bkp-table">
          <colgroup>
            <col style={{ width: colWidths.expand }} />
            <col style={{ width: colWidths.code }} />
            <col style={{ width: colWidths.name }} />
            {showExtraColumns && <col style={{ width: colWidths.source }} />}
            {showExtraColumns && <col style={{ width: colWidths.tag }} />}
            {showExtraColumns && visibleCustomColumns.map((col) => (
              <col key={col.id} style={{ width: customColWidths[col.id] || 150 }} />
            ))}
            {showExtraColumns && !readOnly && <col style={{ width: colWidths.addCol }} />}
            <col style={{ width: colWidths.dimL }} />
            <col style={{ width: colWidths.dimW }} />
            <col style={{ width: colWidths.dimH }} />
            <col style={{ width: colWidths.qty }} />
            <col style={{ width: colWidths.unit }} />
            <col style={{ width: colWidths.price }} />
            <col style={{ width: colWidths.factor }} />
            <col style={{ width: colWidths.total }} />
            {!readOnly && <col style={{ width: colWidths.action }} />}
          </colgroup>
          <thead>
            <tr>
              <th className="expand-col"><ResizeHandle colKey="expand" /></th>
              <th className="code-col">BKP<ResizeHandle colKey="code" /></th>
              <th className="name-col name-col-header">
                <span>Bezeichnung</span>
                <button
                  className="toggle-extra-cols-btn"
                  onClick={() => setShowExtraColumns((v) => !v)}
                  title={showExtraColumns ? "Quelle/Tags ausblenden" : "Quelle/Tags einblenden"}
                >
                  {showExtraColumns ? "\u2212" : "+"}
                </button>
                <ResizeHandle colKey="name" />
              </th>
              {showExtraColumns && <th className="source-col">Quelle<ResizeHandle colKey="source" /></th>}
              {showExtraColumns && (
                <th className="tag-col">Tags<ResizeHandle colKey="tag" /></th>
              )}
              {showExtraColumns && visibleCustomColumns.map((col) => (
                <th key={col.id} className="tag-col custom-col-header">
                  {editingColId === col.id ? (
                    <input
                      type="text"
                      className="col-rename-input"
                      value={editingColName}
                      onChange={(e) => setEditingColName(e.target.value)}
                      onBlur={() => {
                        if (editingColName.trim() && renameCustomColumn) renameCustomColumn(col.id, editingColName.trim());
                        setEditingColId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (editingColName.trim() && renameCustomColumn) renameCustomColumn(col.id, editingColName.trim());
                          setEditingColId(null);
                        } else if (e.key === "Escape") {
                          setEditingColId(null);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <span
                      className="col-header-name"
                      onDoubleClick={() => {
                        if (!readOnly) {
                          setEditingColId(col.id);
                          setEditingColName(col.name);
                        }
                      }}
                    >
                      {col.name}
                    </span>
                  )}
                  {!readOnly && (
                    <button
                      className="col-delete-btn"
                      onClick={() => {
                        if (window.confirm(`Spalte "${col.name}" löschen?`)) deleteCustomColumn(col.id);
                      }}
                      title="Spalte löschen"
                    >
                      ×
                    </button>
                  )}
                  <ResizeHandle colKey={col.id} isCustom />
                </th>
              ))}
              {showExtraColumns && !readOnly && (
                <th className="add-col-header">
                  <input
                    type="text"
                    className="add-col-input"
                    placeholder="+ Spalte..."
                    value={newColName}
                    onChange={(e) => setNewColName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newColName.trim()) {
                        addCustomColumn(newColName.trim());
                        setNewColName("");
                      }
                    }}
                  />
                </th>
              )}
              <th className="dim-col">Länge<ResizeHandle colKey="dimL" /></th>
              <th className="dim-col">Breite<ResizeHandle colKey="dimW" /></th>
              <th className="dim-col">Höhe<ResizeHandle colKey="dimH" /></th>
              <th className="qty-col">Menge<ResizeHandle colKey="qty" /></th>
              <th className="unit-col">Einheit<ResizeHandle colKey="unit" /></th>
              <th className="price-col">EP<ResizeHandle colKey="price" /></th>
              <th className="factor-col">Faktor<ResizeHandle colKey="factor" /></th>
              <th className="total-col">Total<ResizeHandle colKey="total" /></th>
              {!readOnly && <th className="action-col"></th>}
            </tr>
          </thead>
          {stickyAncestors.length > 0 && (
            <tbody className="sticky-breadcrumb" ref={breadcrumbRef}>
              {stickyAncestors.map((code, idx) => {
                const n = effectiveTree[code];
                if (!n) return null;
                const nodeHasChildren = n.children && n.children.length > 0;
                const isAggregatingParent = !n.active && nodeHasChildren && n.totalComputed > 0;
                return (
                  <tr key={`sticky-${code}`}
                    className={`bkp-row level-${n.level} breadcrumb-row breadcrumb-row-${idx}`}
                    onClick={() => {
                      const el = tableContainerRef.current?.querySelector(`tr[data-code="${code}"]`);
                      if (el) el.scrollIntoView({ block: "start" });
                    }}
                  >
                    <td className="expand-cell">
                      {nodeHasChildren && <span className="expand-button">{"\u25BC"}</span>}
                    </td>
                    <td className="code-cell"><span>{code}</span></td>
                    <td className="name-cell">{n.name || ""}</td>
                    {showExtraColumns && <td className="source-cell">{n.source || ""}</td>}
                    {showExtraColumns && <td className="tag-cell"></td>}
                    {showExtraColumns && visibleCustomColumns.map((col) => (
                      <td key={col.id} className="tag-cell"></td>
                    ))}
                    {showExtraColumns && !readOnly && <td></td>}
                    {isAggregatingParent ? (
                      <>{["dim","dim","dim","qty","unit","price","factor"].map((_, i) => <td key={i} className="number-cell"></td>)}</>
                    ) : (
                      <>
                        {renderCell(n, "length", "number")}
                        {renderCell(n, "width", "number")}
                        {renderCell(n, "height", "number")}
                        {renderCell(n, "quantity", "number")}
                        {renderCell(n, "unit", "unit")}
                        {renderCell(n, "unitPrice", "number")}
                        {renderCell(n, "factor", "number")}
                      </>
                    )}
                    <td className="total-cell">{formatSwissCurrency(n.totalComputed)}</td>
                    {!readOnly && <td className="action-cell"></td>}
                  </tr>
                );
              })}
            </tbody>
          )}
          <tbody>
            {renderTree()}
          </tbody>
        </table>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="context-menu"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <div className="context-menu-header">{contextMenu.nodeName}</div>
          <div className="context-menu-item-group">
            {effectiveTree[contextMenu.bkpCode]?.comment ? (
              <>
                <button
                  className="context-menu-item"
                  onClick={() => {
                    startEdit(
                      contextMenu.bkpCode,
                      "comment",
                      effectiveTree[contextMenu.bkpCode]?.comment
                    );
                    setContextMenu(null);
                  }}
                >
                  Kommentar bearbeiten
                </button>
                <button
                  className="context-menu-item context-menu-item-danger"
                  onClick={() => {
                    handleCellEdit(contextMenu.bkpCode, "comment", "");
                    setContextMenu(null);
                  }}
                >
                  Kommentar löschen
                </button>
              </>
            ) : (
              <button
                className="context-menu-item"
                onClick={() => {
                  startEdit(contextMenu.bkpCode, "comment", "");
                  setContextMenu(null);
                }}
              >
                Kommentar hinzufügen
              </button>
            )}
          </div>
          {variants.map((v) => {
            const existingPos = v.positions.find((p) => p.bkpCode === contextMenu.bkpCode);
            return (
              <div key={v.id} className="context-menu-item-group">
                {existingPos ? (
                  <>
                    <button
                      className="context-menu-item"
                      onClick={() => openOverrideModal(v.id, contextMenu.bkpCode)}
                    >
                      Überschreibung bearbeiten ({v.name})
                    </button>
                    <button
                      className="context-menu-item context-menu-item-danger"
                      onClick={() => {
                        if (removePositionFromVariant) {
                          removePositionFromVariant(v.id, contextMenu.bkpCode);
                        }
                        setContextMenu(null);
                      }}
                    >
                      Aus {v.name} entfernen
                    </button>
                  </>
                ) : (
                  <button
                    className="context-menu-item"
                    onClick={() => openOverrideModal(v.id, contextMenu.bkpCode)}
                  >
                    Zu {v.name} hinzufügen
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Override Modal */}
      {overrideModal && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) setOverrideModal(null); }}>
          <div className="modal-content override-modal">
            <div className="modal-header">
              <h3>Varianten-Überschreibung: {overrideModal.nodeName}</h3>
              <button className="modal-close" onClick={() => setOverrideModal(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p className="override-hint">
                Werte ändern, die in dieser Variante überschrieben werden sollen.
              </p>
              {OVERRIDE_FIELDS.map((field) => (
                <div className="form-group" key={field.key}>
                  <label>{field.label}</label>
                  {field.type === "unit" ? (
                    <select
                      value={overrideValues[field.key] || "pl"}
                      onChange={(e) => setOverrideValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    >
                      {UNITS.map((u) => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="number"
                      step="0.01"
                      value={overrideValues[field.key] || 0}
                      onChange={(e) => setOverrideValues((prev) => ({ ...prev, [field.key]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setOverrideModal(null)}
              >
                Abbrechen
              </button>
              <button
                className="btn btn-primary"
                onClick={handleOverrideSave}
              >
                Überschreibung speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimateEditor;
